import React, { useCallback, useEffect, useReducer, useRef, useState, type FormEvent } from "react";
import styled from "styled-components";
import apiClient from "../../lib/apiClient";
import { getProjectList, type Project } from "../../api";
import {
  canIssue,
  displayLink,
  failureText,
  issueReducer,
  keyRows,
  KEYS_PATH,
  MAX_KEY_NAME,
  NO_LINK,
  projectsProblem,
  revokeQuestion,
  type CopyState,
  type DisplayKeys,
  type IssuedKey,
  type KeyRowView,
} from "./displayKeysModel";

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 920px;
  color: var(--text-normal);
  h2 { margin: 0; font-size: 1.2rem; color: var(--text-strong, inherit); }
  p { margin: 0; }
`;

const Hint = styled.p`
  font-size: 0.85rem;
  color: var(--text-muted, #8a93a0);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--border-color, rgba(128, 128, 128, 0.3));
  border-radius: 10px;
  label { display: flex; flex-direction: column; gap: 0.35rem; font-size: 0.9rem; }
  input[type="text"] { padding: 0.5rem 0.6rem; border-radius: 6px; border: 1px solid var(--border-color, rgba(128, 128, 128, 0.4)); background: transparent; color: inherit; font: inherit; }
  fieldset { border: 0; margin: 0; padding: 0; display: flex; flex-wrap: wrap; gap: 0.4rem 1rem; }
  legend { font-size: 0.9rem; margin-bottom: 0.35rem; }
  fieldset label { flex-direction: row; align-items: center; gap: 0.35rem; }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const Action = styled.button<{ $danger?: boolean; $primary?: boolean }>`
  padding: 0.45rem 0.9rem;
  border-radius: 6px;
  border: 1px solid ${(p) => (p.$danger ? "var(--danger, #e5534b)" : "var(--primary-accent, #4ea1ff)")};
  background: ${(p) => (p.$primary ? "var(--primary-accent, #4ea1ff)" : "transparent")};
  color: ${(p) => (p.$primary ? "#fff" : p.$danger ? "var(--danger, #e5534b)" : "var(--primary-accent, #4ea1ff)")};
  font: inherit;
  cursor: pointer;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid var(--primary-accent, #4ea1ff); outline-offset: 2px; }
`;

const Issued = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 1rem;
  border: 2px solid var(--warning, #e3b341);
  border-radius: 10px;
  h3 { margin: 0; font-size: 1rem; }
  input { width: 100%; padding: 0.5rem 0.6rem; font-family: monospace; font-size: 0.85rem; border-radius: 6px; border: 1px solid var(--border-color, rgba(128, 128, 128, 0.4)); background: transparent; color: inherit; }
`;

const Problem = styled.p`
  color: var(--danger, #e5534b);
  font-size: 0.9rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  th, td { padding: 0.5rem 0.6rem; text-align: left; border-bottom: 1px solid var(--border-color, rgba(128, 128, 128, 0.25)); vertical-align: top; }
  thead th { font-weight: 600; color: var(--text-muted, #8a93a0); }
  tr.over { opacity: 0.55; }
  .small { font-size: 0.8rem; color: var(--text-muted, #8a93a0); }
`;

const COPY_WORDS: Record<CopyState, string | null> = {
  none: null,
  copied: "복사했습니다.",
  failed: "복사하지 못했습니다 — 주소를 직접 선택해 복사하세요.",
};

interface IssuedLinkProps {
  name: string;
  link: string;
  copy: CopyState;
  onCopy: () => void;
  onClose: () => void;
}

/** 방금 만든 주소 — 이 창이 유일하게 보여 주는 곳이고, 닫으면 어디에도 남지 않는다. */
function IssuedLink({ name, link, copy, onCopy, onClose }: IssuedLinkProps) {
  const words = COPY_WORDS[copy];
  return (
    <Issued role="group" aria-labelledby="display-issued-heading">
      <h3 id="display-issued-heading">'{name}' 연결 주소</h3>
      <p role="status">이 주소는 지금 한 번만 보입니다. 닫으면 다시 볼 수 없습니다 — 잃어버리면 새로 만드세요.</p>
      <label>
        <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>연결 주소</span>
        {/* 눌렀던 단추가 막히며 초점이 사라진다 — 한 번뿐인 주소로 곧장 옮긴다. */}
        <input type="text" readOnly autoFocus value={link} onFocus={(event) => event.target.select()} />
      </label>
      <Hint>주소를 상황판 화면(벽 TV · 모니터)에 직접 넣으세요. 이 주소를 아는 사람은 누구나 그 프로젝트의 상황판을 볼 수 있으니 메신저나 문서에 남기지 마세요.</Hint>
      <Row>
        <Action type="button" $primary onClick={onCopy}>
          주소 복사
        </Action>
        <Action type="button" onClick={onClose}>
          닫기
        </Action>
        {words && (
          <span role="status" style={{ color: copy === "failed" ? "var(--danger, #e5534b)" : undefined }}>
            {words}
          </span>
        )}
      </Row>
    </Issued>
  );
}

interface KeysTableProps {
  rows: KeyRowView[];
  isBusy: boolean;
  onRevoke: (row: KeyRowView) => void;
}

/** 쓰지 않는 연결을 가려내는 표 — 이름 · 프로젝트 · 끝나는 때 · 마지막 접속을 함께 본다. */
function KeysTable({ rows, isBusy, onRevoke }: KeysTableProps) {
  return (
    <Table>
      <thead>
        <tr>
          <th scope="col">이름</th>
          <th scope="col">보여 주는 프로젝트</th>
          <th scope="col">만든 때</th>
          <th scope="col">끝나는 때</th>
          <th scope="col">마지막 접속</th>
          <th scope="col">
            <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>회수</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} className={row.canRevoke ? undefined : "over"}>
            <th scope="row">{row.name}</th>
            <td className="small">{row.projects}</td>
            <td>{row.made}</td>
            <td>{row.ends}</td>
            <td className="small">{row.lastUsed}</td>
            <td>
              {row.canRevoke && (
                <Action type="button" $danger disabled={isBusy} aria-label={`${row.name} 회수`} onClick={() => onRevoke(row)}>
                  회수
                </Action>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** 설정 › 상황판 연결 — 내가 멤버인 프로젝트를 묶어 벽 화면 주소를 만들고, 목록을 보고, 회수한다. */
export function DisplayKeysPanel() {
  const [keys, setKeys] = useState<DisplayKeys | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadProblem, setLoadProblem] = useState<string | null>(null);
  const [issue, dispatch] = useReducer(issueReducer, NO_LINK);
  const [name, setName] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [revokeProblem, setRevokeProblem] = useState<string | null>(null);
  const isSending = useRef(false);
  const nameInput = useRef<HTMLInputElement>(null);
  /** 마지막으로 보낸 목록 읽기 — 늦게 온 옛 답이 새 목록을 덮지 않게. */
  const readTicket = useRef(0);

  const reload = useCallback(async () => {
    const ticket = ++readTicket.current;
    try {
      const [keyAnswer, projectAnswer] = await Promise.all([apiClient.get<DisplayKeys>(KEYS_PATH), getProjectList()]);
      if (ticket !== readTicket.current) return;
      setKeys(keyAnswer.data);
      setProjects(projectAnswer.data ?? []);
      setLoadProblem(null);
    } catch (error: unknown) {
      if (ticket === readTicket.current) setLoadProblem(failureText(error));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const close = () => {
    dispatch({ type: "closed" });
    nameInput.current?.focus();
  };

  const toggle = (id: string) => setPicked((now) => (now.includes(id) ? now.filter((p) => p !== id) : [...now, id]));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (isSending.current || !canIssue(issue, name, picked)) return;
    isSending.current = true;
    dispatch({ type: "sent" });
    try {
      // (발급 답은 상태에만 — 캐시 · 저장소에 넣지 않는다.)
      const issued = (await apiClient.post<IssuedKey>(KEYS_PATH, { name: name.trim(), projectIds: picked })).data;
      dispatch({ type: "issued", name: issued.key.name, link: displayLink(window.location.origin, issued.secret) });
      setName("");
      setPicked([]);
      void reload();
    } catch (error: unknown) {
      dispatch({ type: "failed", problem: failureText(error) });
    } finally {
      isSending.current = false;
    }
  };

  const copy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      dispatch({ type: "copied" });
    } catch {
      dispatch({ type: "copy_failed" });
    }
  };

  const revoke = async (row: KeyRowView) => {
    if (revoking !== null || !window.confirm(revokeQuestion(row.name))) return;
    setRevoking(row.id);
    setRevokeProblem(null);
    try {
      await apiClient.delete(`${KEYS_PATH}/${encodeURIComponent(row.id)}`);
    } catch (error: unknown) {
      setRevokeProblem(failureText(error));
    } finally {
      setRevoking(null);
      void reload();
    }
  };

  const titles = new Map(projects.map((p) => [p.id, p.title]));
  const rows = keys ? keyRows(keys.keys, titles, Date.now()) : [];
  const pickProblem = picked.length > 0 ? projectsProblem(picked) : null;
  return (
    <Section aria-labelledby="display-keys-heading">
      <h2 id="display-keys-heading">상황판 연결</h2>
      <Hint>로그인 없이 벽에 켜 두는 상황판 화면의 주소를 만듭니다. 고른 프로젝트의 카드 제목 · 상태 · 할 일 수 · 담당 · 마감 · 긴급만 보이고, 설명 · 댓글 · 첨부는 보이지 않습니다. 주소는 90일 뒤 끝나고, 내가 그 프로젝트에서 빠지면 그 프로젝트는 판에서 빠집니다.</Hint>
      <Form onSubmit={(event) => void submit(event)}>
        <label>
          <span>화면 이름</span>
          <input ref={nameInput} type="text" value={name} maxLength={MAX_KEY_NAME} placeholder="예: 3층 회의실" autoComplete="off" onChange={(event) => setName(event.target.value)} />
        </label>
        <fieldset>
          <legend>보여 줄 프로젝트(내가 멤버인 것)</legend>
          {projects.length === 0 && <Hint>멤버인 프로젝트가 없습니다.</Hint>}
          {projects.map((p) => (
            <label key={p.id}>
              <input type="checkbox" checked={picked.includes(p.id)} onChange={() => toggle(p.id)} />
              {p.title}
            </label>
          ))}
        </fieldset>
        {pickProblem && <Problem role="alert">{pickProblem}</Problem>}
        <Row>
          <Action type="submit" $primary disabled={!canIssue(issue, name, picked)}>
            {issue.step === "sending" ? "만드는 중…" : "연결 주소 만들기"}
          </Action>
        </Row>
      </Form>
      {issue.step === "idle" && issue.problem && <Problem role="alert">{issue.problem}</Problem>}
      {issue.step === "shown" && <IssuedLink name={issue.name} link={issue.link} copy={issue.copy} onCopy={() => void copy(issue.link)} onClose={close} />}
      {revokeProblem && <Problem role="alert">{revokeProblem}</Problem>}
      {loadProblem && (
        <Row>
          <Problem role="alert">{loadProblem}</Problem>
          <Action type="button" onClick={() => void reload()}>
            다시 읽기
          </Action>
        </Row>
      )}
      {keys && rows.length === 0 && <Hint>아직 만든 연결이 없습니다.</Hint>}
      {rows.length > 0 && <KeysTable rows={rows} isBusy={revoking !== null} onRevoke={(row) => void revoke(row)} />}
    </Section>
  );
}

export default React.memo(DisplayKeysPanel);
